import React, { useState, useEffect, useRef } from 'react';
import {
    ChevronRight,
    ChevronLeft,
    ChevronDown,
    GitBranch,
    Folder,
    Image as ImageIcon,
    ArrowUp,
    Check,
    AlertCircle,
    Info,
    MoreVertical,
    Github,
    Sparkles,
    X,
    Wifi,
    WifiOff,
    Settings,
    Bell,
    Mail,
    Lock,
    User,
    Plus,
    MessageSquare,
} from 'lucide-react';
import { useWebSocket } from '../../contexts/WebSocketContext';
import AgentStatusRibbon from '../../components/AgentStatusRibbon';
import AgentTimelineDrawer from '../../components/AgentTimelineDrawer';

interface Task {
    id: string;
    title: string;
    summary: string;
    status: 'completed' | 'warning' | 'in-progress';
    model: string;
    repo: string;
    branch: string;
    stats: { additions: number; deletions: number };
    files: Array<{ path: string; additions: number; deletions: number; diff?: string }>;
    chatHistory: Array<{ role: 'user' | 'soma'; content: string; timestamp: Date }>;
    createdAt: Date;
    contextMessage?: string;
}

type View = 'dashboard' | 'task-detail';
type TaskTab = 'summary' | 'diff' | 'chat';

const formatTimeAgo = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks}w`;
    const months = Math.floor(days / 30);
    return `${months}mo`;
};

const getStatusIcon = (status: Task['status']) => {
    switch (status) {
        case 'completed':
            return Check;
        case 'warning':
            return AlertCircle;
        case 'in-progress':
            return Info;
    }
};

const groupTasksByTime = (tasks: Task[]) => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
        thisWeek: tasks.filter(t => t.createdAt >= oneWeekAgo),
        thisMonth: tasks.filter(t => t.createdAt >= oneMonthAgo && t.createdAt < oneWeekAgo),
        older: tasks.filter(t => t.createdAt < oneMonthAgo),
    };
};

const HiveCoreMobile: React.FC = () => {
    const [currentView, setCurrentView] = useState<View>('dashboard');
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [messageInput, setMessageInput] = useState('');
    const [selectedRepo, setSelectedRepo] = useState('beehive-core');
    const [selectedBranch, setSelectedBranch] = useState('Main');
    const [selectedModel, setSelectedModel] = useState('GPT-5');
    const [activeTab, setActiveTab] = useState<'agents' | 'dashboard'>('agents');
    const [rotatingText, setRotatingText] = useState(0);
    const [scrollY, setScrollY] = useState(0);
    const [showTaskDetail, setShowTaskDetail] = useState(false);
    const [detailTab, setDetailTab] = useState<'diff' | 'chat'>('diff');
    const [uploadedImage, setUploadedImage] = useState<File | null>(null);
    const [showRepoDropdown, setShowRepoDropdown] = useState(false);
    const [showBranchDropdown, setShowBranchDropdown] = useState(false);
    const [showModelDropdown, setShowModelDropdown] = useState(false);
    const [showModeDropdown, setShowModeDropdown] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [userName, setUserName] = useState('Dana Patterson');
    const [userEmail, setUserEmail] = useState('dana@sicadvisors.com');
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [taskCompletionNotifications, setTaskCompletionNotifications] = useState(true);
    const [availableRepos, setAvailableRepos] = useState<string[]>([]);
    const [availableBranches, setAvailableBranches] = useState<string[]>([]);
    const [availableModels, setAvailableModels] = useState<string[]>([]);
    const [chatMessages, setChatMessages] = useState<
        Array<{
            role: 'user' | 'assistant';
            content: string;
            status?: 'streaming' | 'complete' | 'error';
            events?: any[];
            kg?: any;
            plan?: any;
            backend?: string;
        }>
    >([]);
    const [wsConnected, setWsConnected] = useState(false);
    const [streamingMessage, setStreamingMessage] = useState<string>('');
    const [executionEvents, setExecutionEvents] = useState<any[]>([]);
    const [chatThreads, setChatThreads] = useState<
        Array<{ id: string; title: string; messages: any[]; createdAt: Date }>
    >([]);
    const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
    const [chatMode, setChatMode] = useState<'auto' | 'answer' | 'plan' | 'act' | 'consensus'>(
        'auto'
    );
    const [showLearningPanel, setShowLearningPanel] = useState(false);
    const [learningMetrics, setLearningMetrics] = useState<any>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchTasks();
        fetchWorkspaceInfo();

        const checkWsConnection = () => {
            try {
                const wsUrl = `${import.meta.env.VITE_WS_URL || 'wss://hivecore-backend-production.up.railway.app'}/presence`;
                const ws = new WebSocket(wsUrl);
                ws.onopen = () => setWsConnected(true);
                ws.onclose = () => setWsConnected(false);
                ws.onerror = () => setWsConnected(false);
                return () => ws.close();
            } catch (e) {
                setWsConnected(false);
            }
        };

        checkWsConnection();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setRotatingText(prev => (prev + 1) % 3);
        }, 6000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatMessages]);

    useEffect(() => {
        const handleScroll = (e: Event) => {
            const target = e.target as HTMLElement;
            setScrollY(target.scrollTop);
        };
        const scrollContainer = document.getElementById('mobile-scroll-container');
        scrollContainer?.addEventListener('scroll', handleScroll);
        return () => scrollContainer?.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchWorkspaceInfo = async () => {
        try {
            const backendUrl =
                import.meta.env.VITE_BACKEND_URL ||
                'https://hivecore-backend-production.up.railway.app';

            const [workspaceRes, reposRes, llmsRes, branchesRes] = await Promise.all([
                fetch(`${backendUrl}/api/arcos/ui/workspace`),
                fetch(`${backendUrl}/api/arcos/ui/repos`),
                fetch(`${backendUrl}/api/arcos/ui/llms`),
                fetch(`${backendUrl}/api/arcos/ui/branches`),
            ]);

            if (workspaceRes.ok) {
                const data = await workspaceRes.json();
                if (data.repo) setSelectedRepo(data.repo);
                if (data.branch) setSelectedBranch(data.branch);
                if (data.llm) setSelectedModel(data.llm);
            }

            if (reposRes.ok) {
                const data = await reposRes.json();
                if (data.repos) setAvailableRepos(data.repos);
            }

            if (llmsRes.ok) {
                const data = await llmsRes.json();
                if (data.llms) setAvailableModels(data.llms);
            }

            if (branchesRes.ok) {
                const data = await branchesRes.json();
                if (data.branches) setAvailableBranches(data.branches);
            }
        } catch (error) {
            console.error('Failed to fetch workspace info:', error);
        }
    };

    const fetchTasks = async () => {
        try {
            const response = await fetch(
                `${import.meta.env.VITE_BACKEND_URL || ''}/api/arcos/ui/tasks`
            );
            if (response.ok) {
                const data = await response.json();
                const formattedTasks: Task[] = (data.data?.tasks || []).map((t: any) => ({
                    id: t.id,
                    title: t.beeName || t.goal || 'Untitled Task',
                    summary: t.summary || `Task ${t.status}`,
                    status:
                        t.status === 'completed'
                            ? 'completed'
                            : t.status === 'failed'
                              ? 'warning'
                              : 'in-progress',
                    model: 'SOMA',
                    repo: t.repo || selectedRepo,
                    branch: t.branch || selectedBranch,
                    stats: {
                        additions: t.stats?.additions || 0,
                        deletions: t.stats?.deletions || 0,
                    },
                    files: t.files || [],
                    chatHistory: t.chatHistory || [],
                    createdAt: new Date(t.createdAt || Date.now()),
                }));
                setTasks(formattedTasks);
            }
        } catch (error) {
            console.error('Failed to fetch tasks:', error);
        }
    };

    const fetchLearningMetrics = async () => {
        try {
            const backendUrl =
                import.meta.env.VITE_BACKEND_URL ||
                'https://hivecore-backend-production.up.railway.app';
            const response = await fetch(`${backendUrl}/api/chat/consensus/metrics?tenant=default`);
            if (response.ok) {
                const data = await response.json();
                setLearningMetrics(data);
            }
        } catch (error) {
            console.error('Failed to fetch learning metrics:', error);
        }
    };

    const handleCreateTask = async () => {
        if (!messageInput.trim() || isLoading) return;
        setIsLoading(true);
        try {
            const response = await fetch(
                `${import.meta.env.VITE_BACKEND_URL || ''}/api/soma/chat`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        message: messageInput,
                        profile: 'soma_dev_pr',
                        repo: selectedRepo,
                        branch: selectedBranch,
                        // SOMA Platform envelope fields (required by requireEnvelope middleware)
                        tenant_id: 'mobile-user',
                        app_id: 'hivecore',
                        user_id: 'anonymous',
                        data_tier: 'INTERNAL',
                        rbac_claims: {
                            roles: ['viewer'],
                            namespaces: ['hivecore://**'],
                            tiers: ['PUBLIC', 'INTERNAL'],
                            capabilities: ['can_read_kb'],
                        },
                    }),
                }
            );
            if (response.ok) {
                setMessageInput('');
                await fetchTasks();
            }
        } catch (error) {
            console.error('Failed to create task:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const rotatingTexts = ['Standing by...', 'Agents ready...', 'Awaiting your command...'];

    const handleCtaClick = () => {
        searchInputRef.current?.focus();
        if (navigator.vibrate) {
            navigator.vibrate(10);
        }
    };

    const handleTaskClick = (task: Task) => {
        setSelectedTask(task);
        setShowTaskDetail(true);
    };

    const handleNavigateToSettings = () => {
        setShowSettings(true);
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setUploadedImage(e.target.files[0]);
        }
    };

    const handleImageButtonClick = () => {
        fileInputRef.current?.click();
    };

    const executeWithSOMA = async (userMessage: string, assistantIndex: number) => {
        const backendUrl =
            import.meta.env.VITE_BACKEND_URL ||
            'https://hivecore-backend-production.up.railway.app';
        const startTime = Date.now();

        try {
            const endpoint = chatMode === 'consensus' ? '/api/chat/consensus' : '/api/chat/stream';
            const url =
                chatMode === 'consensus'
                    ? `${backendUrl}${endpoint}`
                    : `${backendUrl}${endpoint}?mode=${chatMode}&tenant=default&sessionId=${currentThreadId || 'default'}`;

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: userMessage,
                    model: chatMode === 'consensus' ? undefined : selectedModel,
                }),
            });

            if (!response.ok) {
                throw new Error(`Chat stream failed: ${response.statusText}`);
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let currentEvents: any[] = [];
            let finalText = '';
            let currentBackend = '';
            let currentPlan: any = null;
            let currentKG: any = null;
            let currentStatus = '';

            if (reader) {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (line.startsWith('event: ')) {
                            currentStatus = line.substring(7).trim();
                        } else if (line.startsWith('data: ')) {
                            try {
                                const dataStr = line.substring(6).trim();
                                if (!dataStr || currentStatus === 'ping') continue;

                                const data = JSON.parse(dataStr);
                                console.log('[Chat Stream]', currentStatus, data);

                                if (currentStatus === 'backend') {
                                    currentBackend = data;
                                } else if (currentStatus === 'status') {
                                    currentEvents.push({ type: 'status', status: data });
                                } else if (currentStatus === 'plan') {
                                    currentPlan = data;
                                    currentEvents.push({ type: 'plan', plan: data });
                                } else if (currentStatus === 'token') {
                                    finalText += data;
                                } else if (currentStatus === 'kg') {
                                    currentKG = data;
                                } else if (currentStatus === 'cag_verdict') {
                                    currentEvents.push({ type: 'cag_verdict', verdict: data });
                                } else if (currentStatus === 'sources') {
                                    currentEvents.push({ type: 'sources', sources: data });
                                } else if (currentStatus === 'tool') {
                                    currentEvents.push({ type: 'tool', tool: data });
                                } else if (currentStatus === 'error') {
                                    currentEvents.push({ type: 'error', error: data });
                                    finalText = `Error: ${data.message || 'Unknown error'}`;
                                } else if (currentStatus === 'debate') {
                                    currentEvents.push({ type: 'debate', debate: data });
                                } else if (currentStatus === 'confidence') {
                                    currentEvents.push({ type: 'confidence', confidence: data });
                                } else if (currentStatus === 'escalation') {
                                    currentEvents.push({ type: 'escalation', escalation: data });
                                } else if (currentStatus === 'answer') {
                                    finalText = data;
                                } else if (currentStatus === 'warning') {
                                    currentEvents.push({ type: 'warning', warning: data });
                                } else if (currentStatus === 'done') {
                                    currentEvents.push({ type: 'done', done: data });
                                }

                                setChatMessages(prev => {
                                    const updated = [...prev];
                                    updated[assistantIndex] = {
                                        role: 'assistant',
                                        content: finalText || 'Processing...',
                                        status: 'streaming',
                                        events: currentEvents,
                                        backend: currentBackend,
                                        plan: currentPlan,
                                        kg: currentKG,
                                    };
                                    return updated;
                                });
                            } catch (e) {
                                console.error('Failed to parse SSE data:', e);
                            }
                        }
                    }
                }
            }

            const latency = Date.now() - startTime;
            console.log(`[Telemetry] mode=${chatMode}, latency_ms=${latency}`);

            setChatMessages(prev => {
                const updated = [...prev];
                updated[assistantIndex] = {
                    role: 'assistant',
                    content: finalText || 'Task completed',
                    status: 'complete',
                    events: currentEvents,
                    backend: currentBackend,
                    plan: currentPlan,
                    kg: currentKG,
                };
                return updated;
            });
        } catch (error) {
            console.error('Chat stream error:', error);
            throw error;
        }
    };

    const handleNewChat = () => {
        setChatMessages([]);
        setCurrentThreadId(null);
        setActiveTab('agents');
    };

    const handleLoadThread = (threadId: string) => {
        const thread = chatThreads.find(t => t.id === threadId);
        if (thread) {
            setChatMessages(thread.messages);
            setCurrentThreadId(threadId);
            setActiveTab('agents');
        }
    };

    const handleSendMessage = async () => {
        if (!messageInput.trim() && !uploadedImage) return;

        const userMessage = messageInput;
        const newMessage = {
            role: 'user' as const,
            content: userMessage,
        };

        setChatMessages(prev => [...prev, newMessage]);
        setMessageInput('');
        setUploadedImage(null);
        setIsLoading(true);

        const assistantMessageIndex = chatMessages.length + 1;
        setChatMessages(prev => [
            ...prev,
            { role: 'assistant', content: '', status: 'streaming', events: [] },
        ]);

        try {
            await executeWithSOMA(userMessage, assistantMessageIndex);

            const threadTitle =
                chatMessages.length === 0
                    ? userMessage
                    : chatThreads.find(t => t.id === currentThreadId)?.title || userMessage;

            if (!currentThreadId) {
                const newThread = {
                    id: Date.now().toString(),
                    title: userMessage.substring(0, 50) + (userMessage.length > 50 ? '...' : ''),
                    messages: [
                        ...chatMessages,
                        newMessage,
                        {
                            role: 'assistant' as const,
                            content: '',
                            status: 'streaming',
                            events: [],
                        },
                    ],
                    createdAt: new Date(),
                };
                setChatThreads(prev => [newThread, ...prev]);
                setCurrentThreadId(newThread.id);
            } else {
                setChatThreads(prev =>
                    prev.map(t => (t.id === currentThreadId ? { ...t, messages: chatMessages } : t))
                );
            }

            await fetchTasks();
        } catch (error) {
            console.error('Failed to send message:', error);
            setChatMessages(prev => {
                const updated = [...prev];
                updated[assistantMessageIndex] = {
                    role: 'assistant',
                    content: 'Error: Failed to process request',
                    status: 'error',
                };
                return updated;
            });
        } finally {
            setIsLoading(false);
        }
    };

    const switchWorkspace = async (updates: { repo?: string; branch?: string; llm?: string }) => {
        try {
            const backendUrl =
                import.meta.env.VITE_BACKEND_URL ||
                'https://hivecore-backend-production.up.railway.app';
            const response = await fetch(`${backendUrl}/api/arcos/ui/workspace/switch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates),
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Workspace switched:', data);
            }
        } catch (error) {
            console.error('Failed to switch workspace:', error);
        }
    };

    const groupedTasks = groupTasksByTime(tasks);

    return (
        <div
            className="flex flex-col h-screen text-slate-100"
            style={{
                background: 'radial-gradient(ellipse at top, #1a1442 0%, #080a16 100%)',
            }}
        >
            {/* Header with gradient and parallax */}
            <div
                className="flex-shrink-0 border-b px-3 py-2"
                style={{
                    background: 'linear-gradient(180deg, #221c59 0%, #101426 100%)',
                    backgroundPositionY: `${scrollY * 0.02}px`,
                    borderColor: 'rgba(255,255,255,0.06)',
                    boxShadow: '0 1px 0 rgba(255,255,255,0.06)',
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <img
                            src="/hivecore-logo.png"
                            alt="HiveCore"
                            className="object-contain"
                            style={{
                                width: '32px',
                                height: '32px',
                                animation: 'beePulse 5s ease-in-out infinite',
                                filter: 'drop-shadow(0 0 8px rgba(122,92,255,0.4))',
                            }}
                        />
                        <span className="text-base font-semibold text-white">HiveCore</span>
                    </div>
                    <button
                        onClick={handleNavigateToSettings}
                        className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium hover:bg-blue-700 transition-colors"
                    >
                        DP
                    </button>
                </div>
            </div>

            {/* Navigation bar */}
            <div
                className="flex-shrink-0 border-b px-3 py-2"
                style={{
                    background: 'rgba(16,20,38,0.6)',
                    borderColor: 'rgba(255,255,255,0.05)',
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setActiveTab('agents')}
                            className={`text-xs font-semibold tracking-wide transition-colors ${
                                activeTab === 'agents' ? 'text-white' : 'text-slate-400'
                            }`}
                        >
                            AGENTS
                        </button>
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            className={`text-xs font-semibold tracking-wide transition-colors ${
                                activeTab === 'dashboard' ? 'text-white' : 'text-slate-400'
                            }`}
                        >
                            DASHBOARD
                        </button>
                    </div>
                    {activeTab === 'agents' && (
                        <button
                            onClick={handleNewChat}
                            className="px-3 py-1.5 rounded-lg bg-purple-600/20 border border-purple-500/30 text-purple-300 text-xs font-medium hover:bg-purple-600/30 transition-colors flex items-center gap-1.5"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            New Chat
                        </button>
                    )}
                </div>
            </div>
            {/* Search bar with frosted glass and enhanced focus */}
            <div
                className="flex-shrink-0 border-b px-4 py-3 space-y-3"
                style={{
                    background: 'rgba(16,20,38,0.8)',
                    backdropFilter: 'blur(12px)',
                    borderColor: 'rgba(255,255,255,0.05)',
                }}
            >
                <div className="relative">
                    <textarea
                        ref={searchInputRef as any}
                        value={messageInput}
                        onChange={e => setMessageInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask HiveCore to build, fix bugs, explore"
                        rows={2}
                        className="w-full text-slate-100 rounded-xl pl-4 pr-20 py-3 text-sm focus:outline-none transition-all duration-300 resize-none"
                        style={{
                            background: 'rgba(30,35,58,0.9)',
                            border: wsConnected
                                ? '2px solid rgba(34,197,94,0.5)'
                                : '2px solid rgba(239,68,68,0.5)',
                            boxShadow: wsConnected
                                ? '0 0 0 2px rgba(34,197,94,0.3), 0 0 20px rgba(34,197,94,0.4), 0 2px 8px rgba(0,0,0,0.3)'
                                : '0 0 0 2px rgba(239,68,68,0.3), 0 0 20px rgba(239,68,68,0.4), 0 2px 8px rgba(0,0,0,0.3)',
                            minHeight: '56px',
                        }}
                        onFocus={e => {
                            e.target.style.transform = 'scale(1.02)';
                            e.target.style.boxShadow = wsConnected
                                ? '0 0 0 3px rgba(34,197,94,0.5), 0 0 24px rgba(34,197,94,0.5), 0 4px 12px rgba(0,0,0,0.3)'
                                : '0 0 0 3px rgba(239,68,68,0.5), 0 0 24px rgba(239,68,68,0.5), 0 4px 12px rgba(0,0,0,0.3)';
                        }}
                        onBlur={e => {
                            e.target.style.transform = 'scale(1)';
                            e.target.style.boxShadow = wsConnected
                                ? '0 0 0 2px rgba(34,197,94,0.3), 0 0 20px rgba(34,197,94,0.4), 0 2px 8px rgba(0,0,0,0.3)'
                                : '0 0 0 2px rgba(239,68,68,0.3), 0 0 20px rgba(239,68,68,0.4), 0 2px 8px rgba(0,0,0,0.3)';
                        }}
                        disabled={isLoading}
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept="image/*"
                            className="hidden"
                        />
                        <button
                            onClick={handleImageButtonClick}
                            className="p-1.5 text-slate-400 hover:text-slate-200 transition-colors"
                            title="Upload screenshot"
                        >
                            <ImageIcon className="w-5 h-5" />
                        </button>
                        <button
                            onClick={handleSendMessage}
                            disabled={(!messageInput.trim() && !uploadedImage) || isLoading}
                            className="p-2 rounded-lg text-white transition-all duration-150 active:scale-95"
                            style={{
                                background: 'linear-gradient(135deg, #6a4dfb 0%, #4fc3ff 100%)',
                                boxShadow: '0 2px 8px rgba(106,77,251,0.4)',
                            }}
                        >
                            <ArrowUp className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                {uploadedImage && (
                    <div
                        className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg"
                        style={{
                            background: 'rgba(106,77,251,0.2)',
                            border: '1px solid rgba(106,77,251,0.3)',
                        }}
                    >
                        <ImageIcon className="w-4 h-4 text-purple-300" />
                        <span className="text-xs text-purple-200 flex-1 truncate">
                            {uploadedImage.name}
                        </span>
                        <button
                            onClick={() => setUploadedImage(null)}
                            className="text-purple-300 hover:text-white transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
                <div className="flex items-center gap-2 text-xs relative overflow-x-auto">
                    {/* Model Selector */}
                    <div className="relative flex-shrink-0">
                        <button
                            onClick={() => setShowModelDropdown(!showModelDropdown)}
                            className="flex items-center gap-1 text-slate-400 hover:text-slate-200 transition-colors px-1.5 py-0.5 rounded"
                        >
                            <img
                                src="/soma-icon.png"
                                alt="SOMA"
                                className="w-5 h-5 object-contain"
                            />
                            <span className="font-medium text-xs">{selectedModel}</span>
                            <ChevronRight
                                className="w-3 h-3"
                                style={{
                                    transform: showModelDropdown ? 'rotate(90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }}
                            />
                        </button>
                        {showModelDropdown && (
                            <div
                                className="absolute top-full left-0 mt-1 rounded-lg overflow-hidden z-50 min-w-[140px]"
                                style={{
                                    background: 'rgba(30,35,58,0.95)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
                                    backdropFilter: 'blur(12px)',
                                }}
                            >
                                {availableModels.map(model => (
                                    <button
                                        key={model}
                                        onClick={() => {
                                            setSelectedModel(model);
                                            setShowModelDropdown(false);
                                            switchWorkspace({ llm: model });
                                        }}
                                        className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-purple-600/20 transition-colors"
                                    >
                                        {model}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Repo Selector */}
                    <div className="relative flex-shrink-0">
                        <button
                            onClick={() => setShowRepoDropdown(!showRepoDropdown)}
                            className="flex items-center gap-1 text-slate-400 hover:text-slate-200 transition-colors px-1.5 py-0.5 rounded"
                        >
                            <Folder className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[80px] text-xs">{selectedRepo}</span>
                            <ChevronRight
                                className="w-3 h-3"
                                style={{
                                    transform: showRepoDropdown ? 'rotate(90deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }}
                            />
                        </button>
                        {showRepoDropdown && (
                            <div
                                className="absolute top-full left-0 mt-1 rounded-lg overflow-hidden z-50 min-w-[180px]"
                                style={{
                                    background: 'rgba(30,35,58,0.95)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
                                    backdropFilter: 'blur(12px)',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                }}
                            >
                                {availableRepos.map(repo => (
                                    <button
                                        key={repo}
                                        onClick={async () => {
                                            setSelectedRepo(repo);
                                            setShowRepoDropdown(false);
                                            switchWorkspace({ repo });

                                            const backendUrl =
                                                import.meta.env.VITE_BACKEND_URL ||
                                                'https://hivecore-backend-production.up.railway.app';
                                            const branchesRes = await fetch(
                                                `${backendUrl}/api/arcos/ui/branches?repo=${repo}`
                                            );
                                            if (branchesRes.ok) {
                                                const data = await branchesRes.json();
                                                if (data.branches)
                                                    setAvailableBranches(data.branches);
                                            }
                                        }}
                                        className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-purple-600/20 transition-colors"
                                    >
                                        {repo}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Branch Selector */}
                    <div className="relative flex-shrink-0">
                        <button
                            onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                            className="flex items-center gap-1 text-slate-400 hover:text-slate-200 transition-colors px-1.5 py-0.5 rounded"
                        >
                            <GitBranch className="w-3.5 h-3.5" />
                            <span className="text-xs">{selectedBranch}</span>
                            <ChevronRight
                                className="w-3 h-3"
                                style={{
                                    transform: showBranchDropdown
                                        ? 'rotate(90deg)'
                                        : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }}
                            />
                        </button>
                        {showBranchDropdown && (
                            <div
                                className="absolute top-full left-0 mt-1 rounded-lg overflow-hidden z-50 min-w-[140px]"
                                style={{
                                    background: 'rgba(30,35,58,0.95)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
                                    backdropFilter: 'blur(12px)',
                                }}
                            >
                                {availableBranches.map(branch => (
                                    <button
                                        key={branch}
                                        onClick={() => {
                                            setSelectedBranch(branch);
                                            setShowBranchDropdown(false);
                                            switchWorkspace({ branch });
                                        }}
                                        className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-purple-600/20 transition-colors"
                                    >
                                        {branch}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Mode Selector - SoMA 2.0 with AUTO Dropdown + CIL Button */}
                <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-slate-500 font-medium flex-shrink-0">Mode:</span>
                    <div className="flex gap-2 flex-1">
                        {/* AUTO Dropdown */}
                        <div className="relative flex-1">
                            <button
                                onClick={() => setShowModeDropdown(!showModeDropdown)}
                                className="w-full px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-purple-600 to-blue-500 text-white shadow-lg flex items-center justify-between"
                            >
                                <span>
                                    {chatMode === 'auto'
                                        ? 'AUTO'
                                        : chatMode === 'answer'
                                          ? 'Answer'
                                          : chatMode === 'plan'
                                            ? 'Plan'
                                            : chatMode === 'act'
                                              ? 'Act'
                                              : 'Consensus'}
                                </span>
                                <ChevronDown className="w-3 h-3" />
                            </button>
                            {showModeDropdown && (
                                <div
                                    className="absolute top-full left-0 right-0 mt-1 rounded-lg overflow-hidden z-50"
                                    style={{
                                        background: 'rgba(15, 23, 42, 0.95)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        boxShadow: '0 8px 16px rgba(0,0,0,0.4)',
                                        backdropFilter: 'blur(12px)',
                                    }}
                                >
                                    {(['auto', 'answer', 'plan', 'act', 'consensus'] as const).map(
                                        mode => (
                                            <button
                                                key={mode}
                                                onClick={() => {
                                                    setChatMode(mode);
                                                    setShowModeDropdown(false);
                                                }}
                                                className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                                                    chatMode === mode
                                                        ? 'bg-purple-600/30 text-white font-semibold'
                                                        : 'text-slate-300 hover:bg-purple-600/20'
                                                }`}
                                            >
                                                {mode === 'auto'
                                                    ? 'AUTO (Intelligent Routing)'
                                                    : mode === 'answer'
                                                      ? 'Answer'
                                                      : mode === 'plan'
                                                        ? 'Plan'
                                                        : mode === 'act'
                                                          ? 'Act'
                                                          : 'Consensus'}
                                            </button>
                                        )
                                    )}
                                </div>
                            )}
                        </div>

                        {/* CIL Button */}
                        <button
                            onClick={() => setChatMode('consensus')}
                            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                chatMode === 'consensus'
                                    ? 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white shadow-lg'
                                    : 'bg-slate-800/40 text-slate-400 hover:bg-slate-700/40'
                            }`}
                        >
                            CIL
                        </button>
                    </div>
                </div>

                {/* Learning Metrics Button */}
                {chatMode === 'consensus' && (
                    <button
                        onClick={() => {
                            setShowLearningPanel(!showLearningPanel);
                            if (!showLearningPanel && !learningMetrics) {
                                fetchLearningMetrics();
                            }
                        }}
                        className="mt-2 w-full px-3 py-2 rounded-lg text-xs font-medium bg-slate-800/40 text-slate-300 hover:bg-slate-700/40 transition-all flex items-center justify-center gap-2"
                    >
                        <Sparkles className="w-3 h-3" />
                        {showLearningPanel ? 'Hide' : 'Show'} Learning Metrics
                    </button>
                )}

                {/* Learning Metrics Panel */}
                {showLearningPanel && learningMetrics && (
                    <div className="mt-3 p-3 rounded-lg bg-slate-800/60 border border-slate-700/50">
                        <div className="text-xs font-semibold text-slate-300 mb-3 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-purple-400" />
                            Consensus Learning (Last 24h)
                        </div>

                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="bg-slate-900/40 rounded p-2">
                                <div className="text-[10px] text-slate-500 mb-1">
                                    Total Requests
                                </div>
                                <div className="text-lg font-bold text-white">
                                    {learningMetrics.totalRequests || 0}
                                </div>
                            </div>
                            <div className="bg-slate-900/40 rounded p-2">
                                <div className="text-[10px] text-slate-500 mb-1">
                                    Cache Hit Rate
                                </div>
                                <div className="text-lg font-bold text-green-400">
                                    {learningMetrics.totalRequests > 0
                                        ? (
                                              (learningMetrics.cacheHits /
                                                  learningMetrics.totalRequests) *
                                              100
                                          ).toFixed(0)
                                        : 0}
                                    %
                                </div>
                            </div>
                            <div className="bg-slate-900/40 rounded p-2">
                                <div className="text-[10px] text-slate-500 mb-1">
                                    Avg Confidence
                                </div>
                                <div className="text-lg font-bold text-blue-400">
                                    {((learningMetrics.avgConfidence || 0) * 100).toFixed(0)}%
                                </div>
                            </div>
                            <div className="bg-slate-900/40 rounded p-2">
                                <div className="text-[10px] text-slate-500 mb-1">
                                    Escalation Rate
                                </div>
                                <div className="text-lg font-bold text-yellow-400">
                                    {learningMetrics.totalRequests > 0
                                        ? (
                                              (learningMetrics.escalations /
                                                  learningMetrics.totalRequests) *
                                              100
                                          ).toFixed(0)
                                        : 0}
                                    %
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-900/40 rounded p-2 mb-2">
                            <div className="text-[10px] text-slate-500 mb-1">Total Cost (24h)</div>
                            <div className="text-lg font-bold text-purple-400">
                                ${(learningMetrics.totalCost || 0).toFixed(4)}
                            </div>
                        </div>

                        <div className="bg-slate-900/40 rounded p-2">
                            <div className="text-[10px] text-slate-500 mb-2">
                                Confidence Distribution
                            </div>
                            <div className="flex gap-2 text-xs">
                                <div className="flex-1">
                                    <div className="text-green-400 font-medium">High (≥75%)</div>
                                    <div className="text-white">
                                        {learningMetrics.confidenceDistribution?.high || 0}
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="text-yellow-400 font-medium">Med (50-75%)</div>
                                    <div className="text-white">
                                        {learningMetrics.confidenceDistribution?.medium || 0}
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="text-red-400 font-medium">Low (&lt;50%)</div>
                                    <div className="text-white">
                                        {learningMetrics.confidenceDistribution?.low || 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div
                id="mobile-scroll-container"
                className="flex-1 overflow-y-auto p-4"
                style={{
                    paddingBottom: 'max(4rem, env(safe-area-inset-bottom))',
                }}
            >
                {activeTab === 'agents' ? (
                    <div ref={chatContainerRef} className="flex flex-col h-full">
                        {chatMessages.length === 0 ? (
                            <div
                                className="flex flex-col items-center justify-center h-full text-center px-6"
                                style={{
                                    paddingTop: '24px',
                                    paddingBottom: '24px',
                                }}
                            >
                                <div
                                    className="mb-6 relative flex items-center justify-center"
                                    style={{ width: '120px', height: '120px' }}
                                >
                                    <div
                                        className="absolute inset-0 rounded-full"
                                        style={{
                                            background:
                                                'radial-gradient(circle, rgba(106,77,251,0.2) 0%, rgba(79,195,255,0.1) 50%, transparent 70%)',
                                            border: '2px solid rgba(106,77,251,0.3)',
                                            animation: 'circlePulse 3s ease-in-out infinite',
                                            boxShadow:
                                                '0 0 20px rgba(106,77,251,0.4), inset 0 0 20px rgba(79,195,255,0.2)',
                                        }}
                                    />
                                    <div
                                        className="relative z-10"
                                        style={{
                                            animation: 'beeMorph 5s ease-in-out infinite',
                                            filter: 'drop-shadow(0 0 12px rgba(106,77,251,0.5))',
                                        }}
                                    >
                                        <img
                                            src="/hivecore-logo.png"
                                            alt="HiveCore Bee"
                                            className="object-contain"
                                            style={{
                                                width: '72px',
                                                height: '72px',
                                            }}
                                        />
                                    </div>
                                </div>

                                <h1
                                    className="text-2xl font-bold text-white mb-3"
                                    style={{
                                        fontWeight: '700',
                                        letterSpacing: '0.3px',
                                        lineHeight: '1.5',
                                    }}
                                >
                                    Ready when you are
                                </h1>

                                <p
                                    className="text-base text-slate-400 max-w-sm mb-4"
                                    style={{
                                        fontSize: '15px',
                                        lineHeight: '1.5',
                                        color: '#8b94a7',
                                    }}
                                >
                                    Ask HiveCore to build features, fix bugs, or explore code with
                                    natural language
                                </p>

                                <p
                                    className="text-sm italic mb-8 transition-opacity duration-500"
                                    style={{
                                        color: 'rgba(124,132,181,0.9)',
                                        fontSize: '13px',
                                        opacity:
                                            rotatingText === 0 ? 1 : rotatingText === 1 ? 0.6 : 1,
                                    }}
                                >
                                    {rotatingTexts[rotatingText]}
                                </p>

                                <button
                                    onClick={handleCtaClick}
                                    className="relative overflow-hidden rounded-full px-8 py-4 font-semibold text-white text-base transition-all duration-180 active:scale-95"
                                    style={{
                                        background:
                                            'linear-gradient(90deg, #6a4dfb 0%, #4fc3ff 100%)',
                                        boxShadow: '0 0 12px rgba(111,87,255,0.5)',
                                        width: 'calc(100% - 48px)',
                                        maxWidth: '400px',
                                        transitionTimingFunction: 'cubic-bezier(0.22,1,0.36,1)',
                                    }}
                                >
                                    <span className="relative z-10 flex items-center justify-center gap-2">
                                        <Sparkles className="w-5 h-5" />
                                        Start Chatting
                                        <ArrowUp className="w-5 h-5" />
                                    </span>
                                    <div
                                        className="absolute top-0 left-0 w-full h-full"
                                        style={{
                                            background:
                                                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                                            animation: 'shimmer 4s linear infinite',
                                            transform: 'translateX(-150%)',
                                        }}
                                    />
                                </button>
                            </div>
                        ) : (
                            <div className="flex-1 space-y-4">
                                {currentThreadId && (
                                    <div className="space-y-2 mb-4">
                                        <AgentStatusRibbon sessionId={currentThreadId} />
                                        <AgentTimelineDrawer sessionId={currentThreadId} />
                                    </div>
                                )}
                                {chatMessages.map((msg, index) => (
                                    <div
                                        key={index}
                                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div
                                            className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                                                msg.role === 'user'
                                                    ? 'bg-gradient-to-r from-purple-600 to-blue-500 text-white'
                                                    : 'bg-slate-800/60 border border-slate-700/50 text-slate-100'
                                            }`}
                                            style={{
                                                boxShadow:
                                                    msg.role === 'user'
                                                        ? '0 2px 8px rgba(106,77,251,0.3)'
                                                        : '0 2px 8px rgba(0,0,0,0.4)',
                                            }}
                                        >
                                            <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                                {msg.content || 'Processing...'}
                                            </p>

                                            {msg.backend && msg.role === 'assistant' && (
                                                <div className="mt-2 text-xs text-slate-400">
                                                    ⚙️ Using{' '}
                                                    {typeof msg.backend === 'string'
                                                        ? msg.backend
                                                        : `${msg.backend.provider} (${msg.backend.model})`}
                                                </div>
                                            )}

                                            {msg.plan && (
                                                <div className="mt-3 pt-3 border-t border-slate-600/50">
                                                    <div className="text-xs font-semibold text-purple-300 mb-2">
                                                        📋 Plan
                                                    </div>
                                                    <div className="space-y-2">
                                                        {msg.plan.steps?.map(
                                                            (step: any, i: number) => (
                                                                <div
                                                                    key={i}
                                                                    className="flex items-start gap-2 text-xs"
                                                                >
                                                                    <span className="text-purple-400 font-medium">
                                                                        {step.step}.
                                                                    </span>
                                                                    <div className="flex-1">
                                                                        <div className="text-slate-200">
                                                                            {step.action}
                                                                        </div>
                                                                        {step.description && (
                                                                            <div className="text-slate-400 text-xs mt-0.5">
                                                                                {step.description}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {msg.kg && msg.role === 'assistant' && (
                                                <div className="mt-3 pt-3 border-t border-slate-600/50">
                                                    <div className="text-xs font-semibold text-slate-300 mb-2">
                                                        Knowledge Gym
                                                    </div>
                                                    <div className="flex flex-wrap gap-2">
                                                        <div
                                                            className={`px-2 py-1 rounded-full text-xs font-medium ${msg.kg.grounded ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}
                                                        >
                                                            {msg.kg.grounded ? '✅' : '❌'} Grounded
                                                        </div>
                                                        <div
                                                            className={`px-2 py-1 rounded-full text-xs font-medium ${msg.kg.cited ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}
                                                        >
                                                            {msg.kg.cited ? '✅' : '❌'} Cited
                                                        </div>
                                                        {msg.kg.merged !== undefined && (
                                                            <div
                                                                className={`px-2 py-1 rounded-full text-xs font-medium ${msg.kg.merged ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}
                                                            >
                                                                {msg.kg.merged ? '✅' : '⚠️'} Merged
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {msg.events &&
                                                msg.events.some(
                                                    (e: any) =>
                                                        e.type === 'confidence' ||
                                                        e.type === 'escalation' ||
                                                        (e.type === 'done' && e.done?.cached)
                                                ) && (
                                                    <div className="mt-3 pt-3 border-t border-slate-600/50">
                                                        <div className="text-xs font-semibold text-slate-300 mb-2">
                                                            Consensus
                                                        </div>
                                                        <div className="flex flex-wrap gap-2">
                                                            {msg.events
                                                                .filter(
                                                                    (e: any) =>
                                                                        e.type === 'confidence'
                                                                )
                                                                .map((event: any, i: number) => {
                                                                    const confidence =
                                                                        event.confidence?.score ||
                                                                        0;
                                                                    const isHigh =
                                                                        confidence >= 0.75;
                                                                    return (
                                                                        <div
                                                                            key={i}
                                                                            className={`px-2 py-1 rounded-full text-xs font-medium ${isHigh ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}
                                                                        >
                                                                            🧪 Confidence:{' '}
                                                                            {(
                                                                                confidence * 100
                                                                            ).toFixed(0)}
                                                                            %
                                                                        </div>
                                                                    );
                                                                })}
                                                            {msg.events
                                                                .filter(
                                                                    (e: any) =>
                                                                        e.type === 'escalation'
                                                                )
                                                                .map((event: any, i: number) => (
                                                                    <div
                                                                        key={i}
                                                                        className="px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300"
                                                                    >
                                                                        ⚡ Escalated to{' '}
                                                                        {event.escalation?.to}
                                                                    </div>
                                                                ))}
                                                            {msg.events.some(
                                                                (e: any) =>
                                                                    e.type === 'done' &&
                                                                    e.done?.cached
                                                            ) && (
                                                                <div className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300">
                                                                    💾 Cached
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                            {msg.events && msg.events.length > 0 && (
                                                <div className="mt-3 pt-3 border-t border-slate-600/50 space-y-2">
                                                    {msg.events.map((event, i) => (
                                                        <div
                                                            key={i}
                                                            className="flex items-start gap-2 text-xs"
                                                        >
                                                            {event.type === 'tool' ? (
                                                                <>
                                                                    <Sparkles className="w-3 h-3 text-purple-400 flex-shrink-0 mt-0.5" />
                                                                    <span className="text-purple-300">
                                                                        {event.name}
                                                                    </span>
                                                                </>
                                                            ) : event.type === 'status' ? (
                                                                <>
                                                                    <Info className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
                                                                    <span className="text-slate-300">
                                                                        {event.status}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Check className="w-3 h-3 text-green-400 flex-shrink-0 mt-0.5" />
                                                                    <span className="text-slate-300">
                                                                        {event.content?.substring(
                                                                            0,
                                                                            80
                                                                        )}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {msg.status === 'streaming' && (
                                                <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                                                    <div className="flex gap-1">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
                                                        <div
                                                            className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse"
                                                            style={{ animationDelay: '0.2s' }}
                                                        />
                                                        <div
                                                            className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse"
                                                            style={{ animationDelay: '0.4s' }}
                                                        />
                                                    </div>
                                                    <span>SOMA is working...</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {chatThreads.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center px-6">
                                <MessageSquare className="w-16 h-16 text-slate-600 mb-4" />
                                <p className="text-slate-400">No conversations yet.</p>
                                <p className="text-slate-500 text-sm mt-2">
                                    Start a chat in the Agents tab!
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <h3 className="text-xs text-slate-400 mb-3 font-medium uppercase tracking-wider">
                                    Your Conversations
                                </h3>
                                {chatThreads.map((thread, index) => (
                                    <button
                                        key={thread.id}
                                        onClick={() => handleLoadThread(thread.id)}
                                        className="w-full text-left rounded-xl p-3.5 transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]"
                                        style={{
                                            background: 'rgba(30,35,58,0.6)',
                                            border: '1px solid rgba(255,255,255,0.08)',
                                            boxShadow:
                                                '0 2px 8px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.04)',
                                            animation: `fadeSlideUp 200ms ease-out ${index * 50}ms both`,
                                        }}
                                    >
                                        <div className="flex items-start gap-3">
                                            <MessageSquare className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                                    <h4 className="text-sm text-white font-medium truncate">
                                                        {thread.title}
                                                    </h4>
                                                    <ChevronRight className="w-4 h-4 text-slate-500" />
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                                    <span>{formatTimeAgo(thread.createdAt)}</span>
                                                    <span>•</span>
                                                    <span>{thread.messages.length} messages</span>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Task Detail Drawer with Premium Styling */}
            {showTaskDetail && selectedTask && (
                <div
                    className="fixed inset-0 z-50 flex items-end"
                    onClick={() => setShowTaskDetail(false)}
                    style={{
                        background: 'rgba(0,0,0,0.7)',
                        backdropFilter: 'blur(8px)',
                    }}
                >
                    <div
                        className="w-full rounded-t-2xl max-h-[85vh] flex flex-col"
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: 'linear-gradient(180deg, #1a1442 0%, #0f1120 100%)',
                            borderTop: '1px solid rgba(255,255,255,0.1)',
                            boxShadow: '0 -4px 24px rgba(106,77,251,0.2)',
                        }}
                    >
                        {/* Drawer Handle */}
                        <div className="flex justify-center py-3">
                            <div
                                className="w-12 h-1 rounded-full"
                                style={{
                                    background: 'rgba(255,255,255,0.2)',
                                }}
                            />
                        </div>

                        {/* Drawer Header */}
                        <div
                            className="px-4 py-3 border-b flex items-center justify-between"
                            style={{
                                borderColor: 'rgba(255,255,255,0.08)',
                            }}
                        >
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold text-white truncate mb-1">
                                    {selectedTask.title}
                                </h3>
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-green-400">
                                        +{selectedTask.stats?.additions || 0}
                                    </span>
                                    <span className="text-red-400">
                                        -{selectedTask.stats?.deletions || 0}
                                    </span>
                                    <span className="text-slate-500">•</span>
                                    <span className="text-slate-400">
                                        {formatTimeAgo(selectedTask.createdAt)}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <button className="p-2 hover:bg-slate-700/30 rounded-lg transition-colors">
                                    <MoreVertical className="w-5 h-5 text-slate-400" />
                                </button>
                                <button
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all active:scale-95"
                                    style={{
                                        background:
                                            'linear-gradient(135deg, #6a4dfb 0%, #4fc3ff 100%)',
                                        color: 'white',
                                        boxShadow: '0 2px 8px rgba(106,77,251,0.4)',
                                    }}
                                >
                                    <Github className="w-4 h-4" />
                                    Create PR
                                </button>
                            </div>
                        </div>

                        {/* Tab Selector */}
                        <div
                            className="flex border-b px-4"
                            style={{
                                borderColor: 'rgba(255,255,255,0.08)',
                            }}
                        >
                            <button
                                onClick={() => setDetailTab('diff')}
                                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    detailTab === 'diff'
                                        ? 'text-white border-purple-400'
                                        : 'text-slate-400 border-transparent'
                                }`}
                            >
                                Diff
                            </button>
                            <button
                                onClick={() => setDetailTab('chat')}
                                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    detailTab === 'chat'
                                        ? 'text-white border-purple-400'
                                        : 'text-slate-400 border-transparent'
                                }`}
                            >
                                Chat
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 overflow-y-auto px-4 py-4">
                            {detailTab === 'diff' ? (
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="text-xs font-semibold text-purple-300 mb-2 uppercase tracking-wider">
                                            Summary
                                        </h4>
                                        <p className="text-sm text-slate-300 leading-relaxed">
                                            {selectedTask.summary}
                                        </p>
                                    </div>
                                    {selectedTask.files && selectedTask.files.length > 0 && (
                                        <div>
                                            <h4 className="text-xs font-semibold text-purple-300 mb-3 uppercase tracking-wider">
                                                Files Changed
                                            </h4>
                                            <div className="space-y-2">
                                                {selectedTask.files.map((file, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="rounded-lg p-3"
                                                        style={{
                                                            background: 'rgba(30,35,58,0.6)',
                                                            border: '1px solid rgba(255,255,255,0.08)',
                                                        }}
                                                    >
                                                        <p className="text-sm text-slate-200 font-mono truncate mb-1">
                                                            {file.path}
                                                        </p>
                                                        <div className="flex items-center gap-3 text-xs">
                                                            <span className="text-green-400">
                                                                +{file.additions}
                                                            </span>
                                                            <span className="text-red-400">
                                                                -{file.deletions}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {selectedTask.chatHistory &&
                                    selectedTask.chatHistory.length > 0 ? (
                                        selectedTask.chatHistory.map((msg, idx) => (
                                            <div
                                                key={idx}
                                                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div
                                                    className={`max-w-[80%] rounded-lg px-4 py-2.5 ${
                                                        msg.role === 'user'
                                                            ? 'bg-gradient-to-r from-purple-600 to-blue-500 text-white'
                                                            : 'text-slate-200'
                                                    }`}
                                                    style={
                                                        msg.role !== 'user'
                                                            ? {
                                                                  background: 'rgba(30,35,58,0.6)',
                                                                  border: '1px solid rgba(255,255,255,0.08)',
                                                              }
                                                            : {}
                                                    }
                                                >
                                                    <p className="text-sm leading-relaxed">
                                                        {msg.content}
                                                    </p>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-12">
                                            <p className="text-sm text-slate-500">
                                                No chat history available
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Settings Modal */}
            {showSettings && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{
                        background: 'rgba(0,0,0,0.8)',
                        backdropFilter: 'blur(8px)',
                    }}
                    onClick={() => setShowSettings(false)}
                >
                    <div
                        className="w-full max-w-md rounded-2xl overflow-hidden"
                        style={{
                            background: 'linear-gradient(180deg, #221c59 0%, #101426 100%)',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                            border: '1px solid rgba(255,255,255,0.1)',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Settings Header */}
                        <div
                            className="flex items-center justify-between p-6 border-b"
                            style={{ borderColor: 'rgba(255,255,255,0.1)' }}
                        >
                            <div className="flex items-center gap-3">
                                <Settings className="w-6 h-6 text-purple-400" />
                                <h2 className="text-xl font-semibold text-white">Settings</h2>
                            </div>
                            <button
                                onClick={() => setShowSettings(false)}
                                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
                            >
                                <X className="w-5 h-5 text-slate-400" />
                            </button>
                        </div>

                        {/* Settings Content */}
                        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                            {/* Profile Section */}
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide">
                                    Profile
                                </h3>

                                <div className="space-y-3">
                                    <div>
                                        <label className="flex items-center gap-2 text-sm text-slate-300 mb-2">
                                            <User className="w-4 h-4" />
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            value={userName}
                                            onChange={e => setUserName(e.target.value)}
                                            className="w-full px-4 py-2.5 rounded-lg text-white text-sm focus:outline-none transition-all"
                                            style={{
                                                background: 'rgba(30,35,58,0.9)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                            }}
                                            onFocus={e =>
                                                (e.target.style.borderColor =
                                                    'rgba(122,92,255,0.5)')
                                            }
                                            onBlur={e =>
                                                (e.target.style.borderColor =
                                                    'rgba(255,255,255,0.1)')
                                            }
                                        />
                                    </div>

                                    <div>
                                        <label className="flex items-center gap-2 text-sm text-slate-300 mb-2">
                                            <Mail className="w-4 h-4" />
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={userEmail}
                                            onChange={e => setUserEmail(e.target.value)}
                                            className="w-full px-4 py-2.5 rounded-lg text-white text-sm focus:outline-none transition-all"
                                            style={{
                                                background: 'rgba(30,35,58,0.9)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                            }}
                                            onFocus={e =>
                                                (e.target.style.borderColor =
                                                    'rgba(122,92,255,0.5)')
                                            }
                                            onBlur={e =>
                                                (e.target.style.borderColor =
                                                    'rgba(255,255,255,0.1)')
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Notifications Section */}
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide">
                                    Notifications
                                </h3>

                                <div className="space-y-3">
                                    <div
                                        className="flex items-center justify-between p-3 rounded-lg"
                                        style={{ background: 'rgba(30,35,58,0.5)' }}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Bell className="w-5 h-5 text-slate-400" />
                                            <div>
                                                <p className="text-sm font-medium text-white">
                                                    Email Notifications
                                                </p>
                                                <p className="text-xs text-slate-400">
                                                    Receive updates via email
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() =>
                                                setEmailNotifications(!emailNotifications)
                                            }
                                            className="relative w-11 h-6 rounded-full transition-colors"
                                            style={{
                                                background: emailNotifications
                                                    ? '#7a5cff'
                                                    : 'rgba(100,116,139,0.3)',
                                            }}
                                        >
                                            <div
                                                className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform"
                                                style={{
                                                    left: emailNotifications
                                                        ? 'calc(100% - 22px)'
                                                        : '2px',
                                                    transform: emailNotifications
                                                        ? 'translateX(0)'
                                                        : 'translateX(0)',
                                                }}
                                            />
                                        </button>
                                    </div>

                                    <div
                                        className="flex items-center justify-between p-3 rounded-lg"
                                        style={{ background: 'rgba(30,35,58,0.5)' }}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Check className="w-5 h-5 text-slate-400" />
                                            <div>
                                                <p className="text-sm font-medium text-white">
                                                    Task Completion
                                                </p>
                                                <p className="text-xs text-slate-400">
                                                    Notify when tasks are completed
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() =>
                                                setTaskCompletionNotifications(
                                                    !taskCompletionNotifications
                                                )
                                            }
                                            className="relative w-11 h-6 rounded-full transition-colors"
                                            style={{
                                                background: taskCompletionNotifications
                                                    ? '#7a5cff'
                                                    : 'rgba(100,116,139,0.3)',
                                            }}
                                        >
                                            <div
                                                className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform"
                                                style={{
                                                    left: taskCompletionNotifications
                                                        ? 'calc(100% - 22px)'
                                                        : '2px',
                                                    transform: taskCompletionNotifications
                                                        ? 'translateX(0)'
                                                        : 'translateX(0)',
                                                }}
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Security Section */}
                            <div className="space-y-4">
                                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide">
                                    Security
                                </h3>

                                <div
                                    className="p-4 rounded-lg"
                                    style={{
                                        background: 'rgba(30,35,58,0.5)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                    }}
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <Lock className="w-5 h-5 text-slate-400" />
                                        <p className="text-sm font-medium text-white">
                                            Change Password
                                        </p>
                                    </div>
                                    <p className="text-xs text-slate-400 mb-3">
                                        Password management will be available once authentication is
                                        enabled.
                                    </p>
                                    <button
                                        disabled
                                        className="w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors opacity-50 cursor-not-allowed"
                                        style={{
                                            background: 'rgba(122,92,255,0.3)',
                                            color: 'rgba(255,255,255,0.6)',
                                        }}
                                    >
                                        Coming Soon with Auth
                                    </button>
                                </div>
                            </div>

                            {/* Save Button */}
                            <button
                                onClick={() => {
                                    setShowSettings(false);
                                }}
                                className="w-full px-4 py-3 rounded-lg text-sm font-semibold text-white transition-all active:scale-95"
                                style={{
                                    background: 'linear-gradient(135deg, #6a4dfb 0%, #4fc3ff 100%)',
                                    boxShadow: '0 4px 12px rgba(106,77,251,0.4)',
                                }}
                            >
                                Save Settings
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const addMobileAnimations = () => {
    if (typeof document !== 'undefined') {
        const styleId = 'hivecore-mobile-animations';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
        @keyframes beePulse {
          0%, 100% { 
            transform: scale(1); 
            opacity: 0.9; 
          }
          50% { 
            transform: scale(1.05); 
            opacity: 1; 
          }
        }

        @keyframes beeMorph {
          0%, 100% { 
            transform: scale(1); 
          }
          50% { 
            transform: scale(1.05); 
          }
        }

        @keyframes circlePulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.1);
            opacity: 1;
          }
        }

        @keyframes shimmer {
          0% { 
            transform: translateX(-150%); 
          }
          100% { 
            transform: translateX(150%); 
          }
        }

        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `;
            document.head.appendChild(style);
        }
    }
};

addMobileAnimations();

export default HiveCoreMobile;
